// src/components/roles/RoleForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { createRole, updateRole, fetchRole, clearRoleError, clearRoleSuccess } from '../../store/actions/roleActions';
import { fetchPermissions } from '../../store/actions/permissionActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';

const RoleForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedRole, loading, error, success } = useSelector((state) => state.roles);
  const { permissions, loading: permLoading } = useSelector((state) => state.permissions);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedModules, setExpandedModules] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Styles object
  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      marginBottom: '2rem',
    },
    title: {
      fontSize: '1.875rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
      marginBottom: '0.5rem',
    },
    subtitle: {
      fontSize: '0.875rem',
      color: '#6b7280',
      margin: 0,
    },
    
    // Form
    form: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    formSection: {
      padding: '1.5rem',
      borderBottom: '1px solid #e5e7eb',
    },
    sectionTitle: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1.5rem',
    },
    
    // Form fields
    fieldGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem',
      marginBottom: '1rem',
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
    },
    required: {
      color: '#ef4444',
      marginLeft: '0.25rem',
    },
    input: {
      padding: '0.5rem 0.75rem',
      fontSize: '0.875rem',
      color: '#111827',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'all 0.2s',
      width: '100%',
    },
    textarea: {
      padding: '0.5rem 0.75rem',
      fontSize: '0.875rem',
      color: '#111827',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'all 0.2s',
      width: '100%',
      minHeight: '6rem',
      resize: 'vertical',
    },
    inputFocus: {
      borderColor: '#2563eb',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    },
    inputError: {
      borderColor: '#ef4444',
    },
    inputErrorFocus: {
      borderColor: '#ef4444',
      boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.1)',
    },
    errorMessage: {
      fontSize: '0.75rem',
      color: '#ef4444',
      marginTop: '0.25rem',
    },
    
    // Permissions header
    permissionsHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    searchContainer: {
      flex: 1,
      maxWidth: '300px',
    },
    searchInput: {
      width: '100%',
      padding: '0.5rem 1rem 0.5rem 2.5rem',
      fontSize: '0.875rem',
      color: '#111827',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'all 0.2s',
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: '0.75rem center',
      backgroundSize: '1.25rem',
    },
    searchInputFocus: {
      borderColor: '#2563eb',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    },
    
    // Stats
    statsContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    statsBadge: {
      padding: '0.25rem 0.75rem',
      backgroundColor: '#f3f4f6',
      color: '#4b5563',
      fontSize: '0.875rem',
      borderRadius: '9999px',
    },
    statsBadgeSelected: {
      backgroundColor: '#2563eb',
      color: '#ffffff',
    },
    selectAllButton: {
      padding: '0.25rem 0.75rem',
      backgroundColor: '#f3f4f6',
      color: '#4b5563',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    selectAllButtonHover: {
      backgroundColor: '#e5e7eb',
    },
    
    // Permissions grid
    permissionsContainer: {
      maxHeight: '500px',
      overflowY: 'auto',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      padding: '0.5rem',
    },
    moduleGroup: {
      marginBottom: '1rem',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      overflow: 'hidden',
    },
    moduleHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.75rem 1rem',
      backgroundColor: '#f9fafb',
      cursor: 'pointer',
      borderBottom: '1px solid #e5e7eb',
      transition: 'background-color 0.2s',
    },
    moduleHeaderHover: {
      backgroundColor: '#f3f4f6',
    },
    moduleTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    moduleName: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#111827',
    },
    moduleCount: {
      fontSize: '0.75rem',
      color: '#6b7280',
      backgroundColor: '#f3f4f6',
      padding: '0.125rem 0.375rem',
      borderRadius: '9999px',
    },
    moduleCheckbox: {
      width: '1rem',
      height: '1rem',
      accentColor: '#2563eb',
      cursor: 'pointer',
    },
    moduleIcon: {
      width: '1.25rem',
      height: '1.25rem',
      color: '#6b7280',
      transition: 'transform 0.2s',
    },
    moduleIconExpanded: {
      transform: 'rotate(90deg)',
    },
    
    // Permissions grid
    permissionsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
      gap: '0.5rem',
      padding: '1rem',
    },
    permissionItem: {
      display: 'flex',
      alignItems: 'flex-start',
      padding: '0.5rem',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    permissionItemHover: {
      backgroundColor: '#f9fafb',
    },
    permissionItemSelected: {
      backgroundColor: '#eff6ff',
    },
    permissionCheckbox: {
      marginTop: '0.125rem',
      width: '1rem',
      height: '1rem',
      accentColor: '#2563eb',
      cursor: 'pointer',
    },
    permissionContent: {
      marginLeft: '0.5rem',
      flex: 1,
    },
    permissionName: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#111827',
    },
    permissionDescription: {
      fontSize: '0.75rem',
      color: '#6b7280',
      marginTop: '0.125rem',
    },
    permissionMeta: {
      fontSize: '0.625rem',
      color: '#9ca3af',
      marginTop: '0.125rem',
      textTransform: 'uppercase',
    },
    
    // Empty state
    emptyState: {
      padding: '3rem',
      textAlign: 'center',
      color: '#6b7280',
    },
    emptyStateIcon: {
      width: '4rem',
      height: '4rem',
      margin: '0 auto 1rem',
      color: '#d1d5db',
    },
    
    // Loading state
    loadingContainer: {
      padding: '3rem',
      textAlign: 'center',
    },
    
    // Form actions
    formActions: {
      padding: '1.5rem',
      backgroundColor: '#f9fafb',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    submitButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    submitButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    submitButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    
    // Loading overlay
    loadingOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    
    // Responsive
    '@media (max-width: 768px)': {
      permissionsHeader: {
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
      searchContainer: {
        maxWidth: '100%',
      },
    },
  };

  // Fetch permissions on mount
  useEffect(() => {
    console.log('Fetching permissions...');
    dispatch(fetchPermissions({ limit: 500 }));
    
    if (id) {
      console.log('Fetching role data for id:', id);
      dispatch(fetchRole(id));
    } else {
      setInitialLoadComplete(true);
    }
  }, [dispatch, id]);

  // Populate form when selectedRole changes
  useEffect(() => {
    if (id && selectedRole) {
      console.log('Populating form with role data:', selectedRole);
      
      setFormData({
        name: selectedRole.name || '',
        description: selectedRole.description || '',
      });

      // Extract permission IDs from current role
      if (selectedRole.rolePermissions) {
        const permIds = selectedRole.rolePermissions.map(rp => rp.permission.id);
        console.log('Setting selected permissions:', permIds);
        setSelectedPermissions(permIds);
      }

      setInitialLoadComplete(true);
    }
  }, [id, selectedRole]);

  // Group permissions by module
  const groupedPermissions = React.useMemo(() => {
    const grouped = {};
    permissions.forEach(perm => {
      if (!grouped[perm.module]) {
        grouped[perm.module] = [];
      }
      grouped[perm.module].push(perm);
    });
    
    // Sort modules alphabetically
    return Object.keys(grouped)
      .sort()
      .reduce((acc, module) => {
        acc[module] = grouped[module].sort((a, b) => a.name.localeCompare(b.name));
        return acc;
      }, {});
  }, [permissions]);

  // Filter permissions based on search term
  const filteredGroupedPermissions = React.useMemo(() => {
    if (!searchTerm) return groupedPermissions;

    const filtered = {};
    Object.entries(groupedPermissions).forEach(([module, perms]) => {
      const matchingPerms = perms.filter(perm =>
        perm.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (perm.description && perm.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      if (matchingPerms.length > 0) {
        filtered[module] = matchingPerms;
      }
    });
    return filtered;
  }, [groupedPermissions, searchTerm]);

  // Calculate selected counts per module
  const moduleSelectedCounts = React.useMemo(() => {
    const counts = {};
    Object.entries(groupedPermissions).forEach(([module, perms]) => {
      counts[module] = perms.filter(perm => selectedPermissions.includes(perm.id)).length;
    });
    return counts;
  }, [groupedPermissions, selectedPermissions]);

  // Validate form
  const validateField = useCallback((name, value) => {
    const errors = {};

    switch (name) {
      case 'name':
        if (!value) {
          errors.name = 'Role name is required';
        } else if (value.length < 3) {
          errors.name = 'Role name must be at least 3 characters';
        }
        break;
      default:
        break;
    }

    return errors;
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    if (touched[name]) {
      const fieldErrors = validateField(name, value);
      setFormErrors(prev => ({
        ...prev,
        ...fieldErrors,
      }));
    }
  }, [touched, validateField]);

  const handleBlur = useCallback((e) => {
    const { name, value } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true,
    }));

    const fieldErrors = validateField(name, value);
    setFormErrors(prev => ({
      ...prev,
      ...fieldErrors,
    }));
  }, [validateField]);

  const handlePermissionToggle = useCallback((permissionId) => {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  }, []);

  const handleModuleToggle = useCallback((module, modulePermissions) => {
    const modulePermIds = modulePermissions.map(p => p.id);
    const allSelected = modulePermIds.every(id => selectedPermissions.includes(id));

    if (allSelected) {
      setSelectedPermissions(prev => prev.filter(id => !modulePermIds.includes(id)));
    } else {
      setSelectedPermissions(prev => [...new Set([...prev, ...modulePermIds])]);
    }
  }, [selectedPermissions]);

  const handleSelectAll = useCallback(() => {
    const allPermissionIds = permissions.map(p => p.id);
    if (selectedPermissions.length === allPermissionIds.length) {
      setSelectedPermissions([]);
    } else {
      setSelectedPermissions(allPermissionIds);
    }
  }, [permissions, selectedPermissions]);

  const toggleModule = useCallback((module) => {
    setExpandedModules(prev => ({
      ...prev,
      [module]: !prev[module],
    }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    // Validate all fields
    const allErrors = {};
    Object.keys(formData).forEach(key => {
      const fieldErrors = validateField(key, formData[key]);
      Object.assign(allErrors, fieldErrors);
    });

    if (Object.keys(allErrors).length > 0) {
      setFormErrors(allErrors);
      // Set all fields as touched
      const allTouched = {};
      Object.keys(formData).forEach(key => {
        allTouched[key] = true;
      });
      setTouched(allTouched);
      return;
    }

    const submitData = {
      ...formData,
      permissions: selectedPermissions,
    };

    console.log('Submitting role data:', submitData);

    try {
      if (id) {
        await dispatch(updateRole(id, submitData));
      } else {
        await dispatch(createRole(submitData));
      }
      navigate('/admin/roles');
    } catch (error) {
      console.error('Form submission error:', error);
    }
  }, [dispatch, id, formData, selectedPermissions, validateField, navigate]);

  // Show loading state
  if ((loading || permLoading) && !initialLoadComplete) {
    return (
      <div style={styles.loadingOverlay}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          {id ? 'Modifier Role' : 'Créer un nouveau rôle'}
        </h1>
        <p style={styles.subtitle}>
          {id ? 'Mettre à jour les informations et les permissions du rôle' : 'Ajouter un nouveau rôle au système'}
        </p>
      </div>

      {error && <Alert type="error" message={error} onClose={() => dispatch(clearRoleError())} />}
      {success && <Alert type="success" message={success} onClose={() => dispatch(clearRoleSuccess())} />}

      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Basic Information */}
        <div style={styles.formSection}>
          <h2 style={styles.sectionTitle}>Informations de base</h2>
          
          {/* Role Name */}
          <div style={styles.fieldGroup}>
            <label style={styles.label}>
              Nom du rôle <span style={styles.required}>*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g., Admin, Manager, Editor"
              style={{
                ...styles.input,
                ...(touched.name && formErrors.name ? styles.inputError : {}),
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = touched.name && formErrors.name
                  ? styles.inputErrorFocus.borderColor
                  : styles.inputFocus.borderColor;
                e.currentTarget.style.boxShadow = touched.name && formErrors.name
                  ? styles.inputErrorFocus.boxShadow
                  : styles.inputFocus.boxShadow;
              }}
              onBlurCapture={(e) => {
                e.currentTarget.style.borderColor = touched.name && formErrors.name
                  ? styles.inputError.borderColor
                  : '#d1d5db';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
            {touched.name && formErrors.name && (
              <div style={styles.errorMessage}>{formErrors.name}</div>
            )}
          </div>

          {/* Description */}
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Decrivez le rôle et ses responsabilités"
              style={styles.textarea}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = styles.inputFocus.borderColor;
                e.currentTarget.style.boxShadow = styles.inputFocus.boxShadow;
              }}
              onBlurCapture={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>

        {/* Permissions */}
        <div style={styles.formSection}>
          <h2 style={styles.sectionTitle}>Permissions</h2>
          
          {/* Permissions Header */}
          <div style={styles.permissionsHeader}>
            <div style={styles.searchContainer}>
              <input
                type="text"
                placeholder="Rechercher les permissions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = styles.searchInputFocus.borderColor;
                  e.currentTarget.style.boxShadow = styles.searchInputFocus.boxShadow;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
            
            <div style={styles.statsContainer}>
              <span style={styles.statsBadge}>
                Total: {permissions.length}
              </span>
              <span style={{
                ...styles.statsBadge,
                ...(selectedPermissions.length > 0 ? styles.statsBadgeSelected : {}),
              }}>
                Sélectionnés: {selectedPermissions.length}
              </span>
              <button
                type="button"
                onClick={handleSelectAll}
                style={styles.selectAllButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.selectAllButtonHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = styles.selectAllButton.backgroundColor;
                }}
              >
                {selectedPermissions.length === permissions.length ? 'Désélectionner tout' : 'Sélectionner tout'}
              </button>
            </div>
          </div>

          {/* Permissions List */}
          {permLoading ? (
            <div style={styles.loadingContainer}>
              <LoadingSpinner />
            </div>
          ) : (
            <div style={styles.permissionsContainer}>
              {Object.keys(filteredGroupedPermissions).length > 0 ? (
                Object.entries(filteredGroupedPermissions).map(([module, perms]) => {
                  const isExpanded = expandedModules[module] !== false;
                  const moduleSelectedCount = moduleSelectedCounts[module] || 0;
                  const allModuleSelected = moduleSelectedCount === perms.length;
                  
                  return (
                    <div key={module} style={styles.moduleGroup}>
                      {/* Module Header */}
                      <div
                        style={styles.moduleHeader}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = styles.moduleHeaderHover.backgroundColor;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = styles.moduleHeader.backgroundColor;
                        }}
                        onClick={() => toggleModule(module)}
                      >
                        <div style={styles.moduleTitle}>
                          <input
                            type="checkbox"
                            checked={allModuleSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleModuleToggle(module, perms);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            style={styles.moduleCheckbox}
                          />
                          <span style={styles.moduleName}>{module}</span>
                          <span style={styles.moduleCount}>
                            {perms.length} {perms.length === 1 ? 'permission' : 'permissions'}
                          </span>
                        </div>
                        <svg
                          style={{
                            ...styles.moduleIcon,
                            ...(isExpanded ? styles.moduleIconExpanded : {}),
                          }}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>

                      {/* Permissions Grid */}
                      {isExpanded && (
                        <div style={styles.permissionsGrid}>
                          {perms.map((permission) => {
                            const isSelected = selectedPermissions.includes(permission.id);
                            
                            return (
                              <div
                                key={permission.id}
                                style={{
                                  ...styles.permissionItem,
                                  ...(isSelected ? styles.permissionItemSelected : {}),
                                }}
                                onMouseEnter={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.backgroundColor = styles.permissionItemHover.backgroundColor;
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }
                                }}
                                onClick={() => handlePermissionToggle(permission.id)}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {}}
                                  style={styles.permissionCheckbox}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <div style={styles.permissionContent}>
                                  <div style={styles.permissionName}>
                                    {permission.name}
                                  </div>
                                  {permission.description && (
                                    <div style={styles.permissionDescription}>
                                      {permission.description}
                                    </div>
                                  )}
                                  <div style={styles.permissionMeta}>
                                    {permission.action} • {permission.resource || 'any'}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div style={styles.emptyState}>
                  <svg style={styles.emptyStateIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <p>Aucune permission trouvée pour "{searchTerm}"</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div style={styles.formActions}>
          <button
            type="button"
            onClick={() => navigate('/admin/roles')}
            style={styles.cancelButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
            }}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading || permLoading}
            style={{
              ...styles.submitButton,
              ...((loading || permLoading) ? styles.submitButtonDisabled : {}),
            }}
            onMouseEnter={(e) => {
              if (!loading && !permLoading) {
                e.currentTarget.style.backgroundColor = styles.submitButtonHover.backgroundColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && !permLoading) {
                e.currentTarget.style.backgroundColor = styles.submitButton.backgroundColor;
              }
            }}
          >
            {loading ? 'Enregistrement...' : id ? 'Mettre à jour le rôle' : 'Créer un rôle'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RoleForm;